import { useId } from 'react';
import { makeStyles, mergeClasses } from '@fluentui/react-components';
import { shellMedia, shellMetrics, tokens } from '@vigeo/ui';

/**
 * THE ROSTER'S TWO VIEWS, AND THE SWITCH BETWEEN THEM (#689; Figma page 12, frame S1, approved by Julio on 2026-10-12, #648).
 *
 * Table is the view the Roster opens on: the staff table across the page. Map is the roster as a narrow list beside the
 * live map. Which one is showing is in the PAGE ADDRESS and nowhere else (`?view=map`; no word at all means Table), so the
 * browser's Back button and a link sent to a colleague land on the same view. It is never written to the saved preferences
 * (ADR-019 is not touched) and is not remembered between visits: a plain link to the Roster always opens the Table.
 */
export type RosterView = 'table' | 'map';

/** The word in the page address that names the view. */
export const ROSTER_VIEW_PARAM = 'view';
export const ROSTER_VIEW_SWITCH_NAME = 'View';
export const ROSTER_VIEW_LABEL: Record<RosterView, string> = { table: 'Table', map: 'Map' };
const VIEWS: readonly RosterView[] = ['table', 'map'];

/** The view an address asks for. Anything but `map`, a misspelling included, is the Table. */
export const rosterViewOf = (search: URLSearchParams): RosterView => (search.get(ROSTER_VIEW_PARAM) === 'map' ? 'map' : 'table');

/** The same address with the view changed. The Table's address carries no word for it, so it is the plain Roster link. */
export function withRosterView(search: URLSearchParams, view: RosterView): URLSearchParams {
  const next = new URLSearchParams(search);
  if (view === 'map') next.set(ROSTER_VIEW_PARAM, 'map');
  else next.delete(ROSTER_VIEW_PARAM);
  return next;
}

const useStyles = makeStyles({
  // Frame S1: 40 high with 4 of padding, so each choice is 32 high. On a phone it is the full width under the title, 44 high.
  group: {
    display: 'inline-flex', flexShrink: 0, boxSizing: 'border-box', height: shellMetrics.controlHeight, margin: 0, padding: tokens.spacingHorizontalXS,
    backgroundColor: tokens.colorNeutralBackground1, border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke1}`, borderRadius: tokens.borderRadiusMedium,
    [shellMedia.phone]: { display: 'flex', order: 1, flexBasis: '100%', height: shellMetrics.touchTarget },
  },
  option: { position: 'relative', display: 'flex', minWidth: 0, cursor: 'pointer', [shellMedia.phone]: { flexGrow: 1, flexBasis: 0 } },
  /**
   * A real radio button, laid over its choice and not painted: the browser gives it the arrow keys and tells a screen reader
   * "Table, 1 of 2". It reaches the top and bottom of the control (over the padding and the edge), so the thing pressed is as
   * tall as the control: 40, and 44 on a phone.
   */
  input: {
    position: 'absolute', top: `calc(0rem - ${tokens.spacingHorizontalXS} - ${tokens.strokeWidthThin})`, left: 0,
    width: '100%', height: `calc(100% + 2 * ${tokens.spacingHorizontalXS} + 2 * ${tokens.strokeWidthThin})`, margin: 0, opacity: 0, cursor: 'pointer',
    ':focus-visible + span': { outline: `${tokens.strokeWidthThick} solid ${tokens.colorStrokeFocus2}`, outlineOffset: tokens.strokeWidthThin },
  },
  face: {
    flexGrow: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box', minWidth: '4rem', paddingInline: tokens.spacingHorizontalL,
    borderRadius: tokens.borderRadiusSmall, color: tokens.colorNeutralForeground1,
    fontFamily: tokens.fontFamilyBase, fontSize: tokens.fontSizeBase300, lineHeight: tokens.lineHeightBase300, fontWeight: tokens.fontWeightSemibold,
  },
  faceChosen: { backgroundColor: tokens.colorBrandBackground, color: tokens.colorNeutralForegroundOnBrand },
});

export interface RosterViewSwitchProps {
  readonly view: RosterView;
  readonly onView: (next: RosterView) => void;
  readonly className?: string;
}

/**
 * One control with two choices, "Table | Map" (frame S1). It is a group of two radio buttons, so the keyboard reaches it
 * once and the arrow keys move between the choices, and it is named "View" for a screen reader.
 */
export function RosterViewSwitch({ view, onView, className }: RosterViewSwitchProps) {
  const styles = useStyles();
  const name = useId();
  return (
    <div className={mergeClasses(styles.group, className)} role="radiogroup" aria-label={ROSTER_VIEW_SWITCH_NAME} data-roster-view={view}>
      {VIEWS.map((choice) => (
        <label key={choice} className={styles.option}>
          <input type="radio" className={styles.input} name={name} value={choice} checked={view === choice} onChange={() => onView(choice)} />
          <span className={mergeClasses(styles.face, view === choice && styles.faceChosen)}>{ROSTER_VIEW_LABEL[choice]}</span>
        </label>
      ))}
    </div>
  );
}
